import { FC, PropsWithChildren, ReactNode } from 'react';

import { HttpError } from '@bonusly/http';
import { Permission, usePermission } from '@bonusly/who-am-i';

type RequirePermissionProps = PropsWithChildren & {
	permission: Permission;
	/** HTTP status when user lacks permission (default 403). Use 404 to hide the resource. */
	statusCode?: number;
	/** Rendered instead of throwing when the user lacks the permission (e.g. an upgrade panel). Takes precedence over statusCode. */
	fallback?: ReactNode;
};

/**
 * Renders children only when the current user has the given permission.
 * Otherwise, if a `fallback` is provided it is rendered instead; if not, throws
 * HttpError with the given statusCode (default 403).
 * Use for route-level enforcement of plan/feature permissions (e.g. CanViewExpandedReporting),
 * passing a `fallback` to show an upgrade panel rather than blocking the route.
 */
export const RequirePermission: FC<RequirePermissionProps> = ({ children, permission, statusCode = 403, fallback }) => {
	const hasPermission = usePermission(permission);

	if (hasPermission) {
		return <>{children}</>;
	}

	if (fallback !== undefined) {
		return <>{fallback}</>;
	}

	throw new HttpError(statusCode);
};
